/**
 * Unique Email validation module for The Events Calendar attendees.
 */

const UNIQUE_EMAIL_OVERRIDE_CLASS = 'crsc-override-unique-email-checkbox';

export const initUniqueEmailValidation = () => {
	const observer = new MutationObserver( () => {
		setupEmailValidation();
	} );

	observer.observe( document.body, { childList: true, subtree: true } );

	// Initial setup in case content is already present.
	setupEmailValidation();
};

const setupEmailValidation = () => {
	const emailInputs = document.querySelectorAll<HTMLInputElement>( '.tribe-tickets__attendee-tickets [name*="[email]"]' );
	emailInputs.forEach( ( input ) => {
		if ( ( input as any )._hasEmailValidation ) return;

		const handler = () => validateAllEmails();
		input.addEventListener( 'input', handler );
		input.addEventListener( 'change', handler );
		( input as any )._hasEmailValidation = true;
	} );
};

/**
 * Addsto the form footer of the registration form a
 * checkbox that lets the customer override the unique email check for attendees they cannot
 * supply a different email address for (e.g. an office administrator registering multiple
 * attendees without knowing each one's email address). Shown to the customer once they receive the unique email error.
 *
 */
const ensureUniqueEmailOverrideCheckbox = ( container: Element ) => {
	const scope: ParentNode = container.closest( 'form' ) || document;
	const footer = scope.querySelector<HTMLElement>( '.tribe-tickets__registration-footer' );

	if ( ! footer || footer.querySelector( `.${UNIQUE_EMAIL_OVERRIDE_CLASS}` ) ) return;

	const submitButton = footer.querySelector( '.tribe-tickets__registration-submit' );

	const containerDiv = document.createElement( 'div' );
	containerDiv.className = 'crsc-override-unique-email-container';
	containerDiv.style.width = '100%';
	containerDiv.style.margin = '10px 0';

	const checkbox = document.createElement( 'input' );
	checkbox.type = 'checkbox';
	checkbox.className = UNIQUE_EMAIL_OVERRIDE_CLASS;
	checkbox.id = `crsc-override-unique-email-${Math.random().toString( 36 ).slice( 2, 9 )}`;
	checkbox.style.marginRight = '5px';

	const label = document.createElement( 'label' );
	label.htmlFor = checkbox.id;
	label.textContent = "I'm not sure of each attendee's email address and would like to use the same email address for multiple attendees for this class.";

	containerDiv.appendChild( checkbox );
	containerDiv.appendChild( label );

	if ( submitButton ) {
		footer.insertBefore( containerDiv, submitButton );
	} else {
		footer.appendChild( containerDiv );
	}

	checkbox.addEventListener( 'change', () => validateAllEmails() );
};

/**
 * Determines whether the unique email override checkbox has been checked for the form
 * that contains the given container/element.
 */
const isUniqueEmailOverrideChecked = ( element: Element ): boolean => {
	const form = element.closest( 'form' );
	const scope: ParentNode = form || document;
	const checkbox = scope.querySelector<HTMLInputElement>( `.${UNIQUE_EMAIL_OVERRIDE_CLASS}` );
	return !! checkbox?.checked;
};

const validateAllEmails = () => {
	const allInputs = document.querySelectorAll<HTMLInputElement>( '.tribe-tickets__attendee-tickets [name*="[email]"]' );
	const eventGroups = new Map<Element, HTMLInputElement[]>();

	// Group inputs by their closest event container.
	allInputs.forEach( ( input ) => {
		const container = input.closest( '.tribe-tickets__registration-event' ) || document.body;
		if ( ! eventGroups.has( container ) ) {
			eventGroups.set( container, [] );
		}
		eventGroups.get( container )?.push( input );
	} );

	eventGroups.forEach( ( groupInputs, container ) => {
		const overrideChecked = isUniqueEmailOverrideChecked( container );
		const emailMap: Record<string, HTMLInputElement[]> = {};

		groupInputs.forEach( ( input ) => {
			const val = input.value.trim().toLowerCase();
			if ( val ) {
				if ( ! emailMap[val] ) {
					emailMap[val] = [];
				}
				emailMap[val].push( input );
			}
		} );

		const hasDuplicates = Object.values( emailMap ).some( ( inputs ) => inputs.length > 1 );
		if ( hasDuplicates && ! overrideChecked ) {
			ensureUniqueEmailOverrideCheckbox( container );
		}

		groupInputs.forEach( ( input ) => {
			const val = input.value.trim().toLowerCase();
			if ( ! overrideChecked && val && emailMap[val].length > 1 ) {
				const message = 'This email address is already used for another attendee in this event.';
				input.setCustomValidity( message );
				showError( input, message );
			} else {
				input.setCustomValidity( '' );
				removeError( input );
			}
		} );
	} );
};

const showError = ( input: HTMLInputElement, message: string ) => {
	let error = input.parentNode?.querySelector( '.crsc-email-error' );
	if ( ! error ) {
		error = document.createElement( 'div' );
		error.className = 'crsc-email-error';
		error.setAttribute( 'style', 'color: #d63638; font-size: 14px; margin-top: 5px; margin-bottom: 5px; font-weight: 600;' );
		input.parentNode?.insertBefore( error, input );
	}
	error.textContent = message;
	input.style.borderColor = '#d63638';
};

const removeError = ( input: HTMLInputElement ) => {
	const error = input.parentNode?.querySelector( '.crsc-email-error' );
	if ( error ) {
		error.remove();
	}
	input.style.borderColor = '';
};
